// src/fleet-stats/analytics/buildHeadline.js
//
// Knaflic "Big Idea": the page's takeaway as one big sentence plus a context
// strip. This IS the summary layer — there is no separate KPI row repeating
// the same numbers. Returns tone-tagged segments so the component colors
// semantically; `action` marks clickable clauses.
//
// tones: "plain" | "value" | "good" | "warn" | "crit"

function isoDaysBetween(aIso, bIso) {
    return Math.round((new Date(bIso + "T00:00:00Z") - new Date(aIso + "T00:00:00Z")) / 86400000);
}

function fmtDayRange(aIso, bIso) {
    if (!aIso || !bIso) return "";
    const opts = { month: "short", day: "numeric", timeZone: "UTC" };
    const a = new Date(aIso + "T00:00:00Z");
    const b = new Date(bIso + "T00:00:00Z");
    const aStr = a.toLocaleDateString("en-US", opts);
    const bStr = b.toLocaleDateString("en-US", opts);
    if (aIso === bIso) return aStr;
    if (a.getUTCMonth() === b.getUTCMonth()) return `${aStr}–${b.getUTCDate()}`;
    return `${aStr} – ${bStr}`;
}

export function buildHeadline({ liveRobots, fieldRobotCount, inFieldAttention, fleetHealth, heroTrends, maxDate, todayIso }) {
    const segments = [];
    const context = [];
    const push = (text, tone = "plain", action = null) => segments.push({ text, tone, action });

    if (!maxDate) {
        push("No data loaded.");
        return { segments, context };
    }

    // Staleness alarm: the batch normally lands daily, so a gap of 3+ days
    // means the pipeline is broken, not the fleet.
    const staleDays = todayIso ? isoDaysBetween(maxDate, todayIso) : 0;
    if (staleDays >= 3) {
        push(`Data is ${staleDays} days old — `, "warn");
    }

    if (liveRobots.count === 0 && fieldRobotCount === 0) {
        push("No robot activity in this scope in the last 7 days of data.");
        context.push({ text: `data through ${maxDate}`, tone: "plain" });
        return { segments, context };
    }

    // ── Line 1: activity + attention ─────────────────────────────────────────
    // Numbers carry the emphasis and the color; the words stay quiet.
    if (liveRobots.count === 0) {
        push("None", "crit");
        push(` of the ${fieldRobotCount} robots in the field drove in the last 7 days of data`);
    } else {
        push(String(liveRobots.count), "value");
        push(` of ${fieldRobotCount} robots in the field drove in the last 7 days `);
        push(`(${fmtDayRange(liveRobots.cutoff, liveRobots.latest)})`, "muted");
    }

    const n = inFieldAttention.length;
    const critical = inFieldAttention.filter((e) => e.severity === "critical").length;
    if (n > 0) {
        push(" — ");
        push(String(n), critical > 0 ? "crit" : "warn", "attention");
        push(` need${n === 1 ? "s" : ""} attention`, "plain", "attention");
        if (critical > 0) {
            push(", ", "plain", "attention");
            push(String(critical), "crit", "attention");
            push(" critical", "plain", "attention");
        }
        push(".");
    } else {
        push(" — ");
        push("no exceptions", "good");
        push(".");
    }

    // ── Line 2: context strip ────────────────────────────────────────────────
    // Deliberately uncolored — alarm color lives in the headline clause and on
    // the board, nowhere else.
    const chg = heroTrends?.km?.changePct;
    if (chg !== null && chg !== undefined && heroTrends.km.previous > 0) {
        const pct = Math.round(Math.abs(chg) * 100);
        context.push({ text: pct === 0 ? "distance flat vs previous week" : `distance ${chg >= 0 ? "up" : "down"} ${pct}% vs previous week` });
    }
    if (fleetHealth && fleetHealth.median !== null) {
        context.push({ text: `median readiness ${fleetHealth.median}/100 (${fleetHealth.band.label.toLowerCase()})` });
    }
    context.push({ text: `daily batch · data through ${maxDate}` });

    return { segments, context };
}
